/**
 * "That went to a person" — what a `$` call answers when it was held rather than run.
 *
 * `ARCHITECTURE.md` §Scopes: a money action over the caller's limit "is not executed —
 * the prepared payload becomes a `review` task for the agent's owner and the tool
 * returns `{ status: 'needs_review', task_ref }`". The studio door answers 202, which
 * is a **success**, so nothing throws and a form that only rendered `isError` would
 * say "done" about work that has not happened.
 *
 * Ochre rather than red, and that is the whole design of it: nothing is broken, and
 * the next move is somebody's. The link is the point — a notice that named a task
 * reference and did not go to it would make a person search the board for it.
 */

import { Link } from '@tanstack/react-router'

import type { NeedsReviewResult } from '@fareast/shared'

import { Notice } from '../portal/Status.js'
import { Linkish, Ref } from '../portal/Text.js'

export type NeedsReviewNoticeProps = {
  readonly review: NeedsReviewResult
}

export function NeedsReviewNotice({ review }: NeedsReviewNoticeProps) {
  return (
    <Notice need role="status">
      Nothing has run. It needs a person first, so it is now{' '}
      <Linkish asChild>
        <Link to="/studio/tasks/$ref" params={{ ref: review.task_ref }}>
          <Ref value={review.task_ref} />
        </Link>
      </Linkish>
      : {review.reason}
    </Notice>
  )
}
